import { useMemo, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import BiomarkerCard from "@/components/results/BiomarkerCard";
import { Search } from "lucide-react";

const CATEGORY_ORDER = [
  "hematology",
  "lipids",
  "glucose",
  "liver",
  "kidney",
  "minerals",
  "vitamins",
  "thyroid",
  "other",
];

export default function BiomarkersSection({ biomarkers, embedded = false }) {
  const { t } = useLang();
  const [query, setQuery] = useState("");
  const [onlyAltered, setOnlyAltered] = useState(false);
  const [activeCat, setActiveCat] = useState("all");

  const cats = useMemo(() => {
    const set = new Set(biomarkers.map((b) => b.category));
    return CATEGORY_ORDER.filter((c) => set.has(c));
  }, [biomarkers]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return biomarkers.filter((b) => {
      if (onlyAltered && b.isNormal) return false;
      if (activeCat !== "all" && b.category !== activeCat) return false;
      if (q && !`${b.name} ${b.code}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [biomarkers, query, onlyAltered, activeCat]);

  const grouped = useMemo(() => {
    const byCat = {};
    for (const b of filtered) {
      if (!byCat[b.category]) byCat[b.category] = [];
      byCat[b.category].push(b);
    }
    return CATEGORY_ORDER.filter((c) => byCat[c]).map((c) => ({
      key: c,
      items: byCat[c],
    }));
  }, [filtered]);

  return (
    <section
      id={embedded ? undefined : "biomarkers"}
      data-testid="biomarkers-section"
      className={embedded ? "" : "container-x scroll-mt-24 py-20 md:py-24"}
    >
      {!embedded && (
        <div className="flex flex-col gap-3">
          <span className="overline">{t.results.biomarkers.overline}</span>
          <h2 className="text-4xl text-zinc-900 md:text-[44px]">{t.results.biomarkers.title}</h2>
          <p className="max-w-2xl text-base text-zinc-600">{t.results.biomarkers.subtitle}</p>
        </div>
      )}

      <div className={`flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between ${embedded ? "" : "mt-8"}`}>
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          <CatChip
            label={t.results.biomarkers.all}
            active={activeCat === "all"}
            onClick={() => setActiveCat("all")}
            testId="cat-chip-all"
          />
          {cats.map((c) => (
            <CatChip
              key={c}
              label={t.results.categories[c] || c}
              active={activeCat === c}
              onClick={() => setActiveCat(c)}
              testId={`cat-chip-${c}`}
            />
          ))}
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              placeholder={t.results.biomarkers.search}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full rounded-full border border-zinc-200 bg-white py-2.5 pl-10 pr-4 text-sm text-zinc-900 outline-none transition focus:border-[color:var(--sentinel)] sm:w-72"
              data-testid="biomarker-search"
            />
          </div>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-zinc-200 bg-white px-3.5 py-2 text-sm text-zinc-700">
            <input
              type="checkbox"
              checked={onlyAltered}
              onChange={(e) => setOnlyAltered(e.target.checked)}
              className="h-4 w-4 accent-[color:var(--sentinel)]"
              data-testid="biomarker-altered-toggle"
            />
            {t.results.biomarkers.onlyAltered}
          </label>
        </div>
      </div>

      <div className="mt-10 space-y-12">
        {grouped.length === 0 && (
          <p className="text-sm text-zinc-500">{t.results.biomarkers.empty}</p>
        )}
        {grouped.map((g) => (
          <div key={g.key} data-testid={`bio-group-${g.key}`}>
            <div className="mb-4 flex items-center gap-3">
              <h3 className="text-2xl text-zinc-900">
                {t.results.categories[g.key] || g.key}
              </h3>
              <span className="rounded-full bg-zinc-100 px-2.5 py-0.5 font-mono text-xs text-zinc-500">
                {g.items.length}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {g.items.map((b, i) => (
                <BiomarkerCard key={`${b.code}-${b.name}-${i}`} b={b} idx={i} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function CatChip({ label, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex shrink-0 items-center rounded-full border px-3.5 py-1.5 text-xs font-semibold transition ${
        active
          ? "border-[color:var(--sentinel)] bg-[color:var(--sentinel)] text-white"
          : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300"
      }`}
    >
      {label}
    </button>
  );
}
